"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="27910" className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center animate-in fade-in zoom-in duration-300">
        <div className="mx-auto bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mb-4">
          <Send className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-2">
          Message Received!
        </h3>
        <p className="text-muted-foreground">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-muted/50 border-border/50 shadow-none focus-visible:ring-2 focus-visible:ring-primary h-12 rounded-xl" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@zapgo!.nl" {...field} className="bg-muted/50 border-border/50 shadow-none focus-visible:ring-2 focus-visible:ring-primary h-12 rounded-xl" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-medium">
              Phone Number <span className="text-muted-foreground text-xs font-normal">(Optional)</span>
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+31 6 1234 5678" {...field} className="bg-muted/50 border-border/50 shadow-none focus-visible:ring-2 focus-visible:ring-primary h-12 rounded-xl" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-medium">Message</FormLabel>
            <FormControl>
              <textarea placeholder="How can we help your business or charging needs?" {...field} className="flex min-h-[150px] w-full rounded-xl bg-muted/50 border border-border/50 px-3 py-3 text-sm shadow-none outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 resize-y" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 text-destructive rounded-xl text-sm border border-destructive/20">
          {submitError}
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-primary hover:bg-primary/90 text-primary-foreground px-10 py-6 text-base font-bold transition-all duration-300 shadow-lg shadow-primary/20 rounded-full">
          {isSubmitting ? <>
              <Loader className="mr-2 size-5 animate-spin" />
              Powering through...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}
export default function NeemContactOp() {
  return <section id="contact" className="bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {}
          <div className="space-y-10">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold uppercase tracking-widest">
                <Clock className="size-3" />
                Response Time: &lt; 2 hours
              </div>
              <h2 className="text-4xl md:text-6xl font-extrabold tracking-tighter text-foreground">
                Get in <span className="text-primary">Touch</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                Need help with a rental or interested in hosting a ZapGO! station? 
                Our team is ready to power up your experience.
              </p>
            </div>

            <div className="grid gap-8">
              <Card className="bg-transparent border-none shadow-none p-0">
                <CardContent className="p-0 flex items-start gap-5">
                  <div className="bg-primary/10 p-4 rounded-2xl shrink-0 mt-1">
                    <MapPin className="size-6 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-bold text-xl text-foreground">Headquarters</h3>
                    <p className="text-muted-foreground leading-relaxed">ZapGO! HQ Amsterdam<br />Straat , 1111AA</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-transparent border-none shadow-none p-0">
                <CardContent className="p-0 flex items-start gap-5">
                  <div className="bg-primary/10 p-4 rounded-2xl shrink-0 mt-1">
                    <Phone className="size-6 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-bold text-xl text-foreground">Phone Support</h3>
                    <p className="text-muted-foreground font-mono">
                      +31 (0) 20 123 4567
                    </p>
                    <p className="text-sm text-muted-foreground/80 mt-1">Available Mon-Fri, 09:00 - 22:00 CET</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-transparent border-none shadow-none p-0">
                <CardContent className="p-0 flex items-start gap-5">
                  <div className="bg-primary/10 p-4 rounded-2xl shrink-0 mt-1">
                    <Mail className="size-6 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-bold text-xl text-foreground">Email Us</h3>
                    <p className="text-muted-foreground font-mono">support@zapgo.nl</p>
                    <p className="text-sm text-muted-foreground/80 mt-1">We typically respond within 2 hours.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {}
          <div className="bg-card rounded-3xl p-8 md:p-12 shadow-2xl shadow-primary/5 border border-border/50 backdrop-blur-sm">
            <div className="mb-10">
              <h3 className="text-3xl font-bold text-foreground mb-3">Send a Message</h3>
              <p className="text-muted-foreground">
                Fill out the form below and our energy experts will get back to you immediately.
              </p>
            </div>

            <FormProvider formKey="contact_form_support" formId="contact_form_support" sectionName="contact-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true
            }
          }} submitText="Send Message" successMessage="Thanks for reaching out! We've received your inquiry and will be in touch shortly.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}